/**
 * Utilitaires centralisés pour le traitement des fichiers Excel
 * Évite la duplication de code entre les parsers de sheets
 */

/**
 * Trouve l'index d'une colonne dans le header
 * @param {Array} headerRow - Ligne de headers
 * @param {string} columnName - Nom de la colonne recherchée
 * @returns {number} Index de la colonne, ou -1 si non trouvée
 */
export function findColumnIndex(headerRow, columnName) {
  return headerRow.findIndex((header) => String(header).trim().toLowerCase() === columnName.toLowerCase());
}

/**
 * Détecte les colonnes événements (colonnes numériques 1, 2, 3, 4...)
 * @param {Array} headerRow - Ligne de headers
 * @returns {Array} Tableau d'objets {index, number} triés par numéro
 *
 * Pourquoi cette détection dynamique ?
 * Les colonnes événements ne commencent pas au même index selon les sheets
 * (Atténuations/Pentes ont des colonnes calculées avant, Longueurs/Positions non)
 */
export function detectEventColumns(headerRow) {
  const eventColumns = [];

  headerRow.forEach((header, index) => {
    const headerStr = String(header).trim();
    // Vérifier si c'est un nombre pur (1, 2, 3...)
    if (/^\d+$/.test(headerStr)) {
      eventColumns.push({
        index,
        number: parseInt(headerStr, 10),
      });
    }
  });

  // Trier par numéro pour garantir l'ordre 1, 2, 3...
  eventColumns.sort((a, b) => a.number - b.number);

  return eventColumns;
}

/**
 * Normalise une chaîne de caractères (trim + uppercase)
 * @param {any} value - Valeur à normaliser
 * @returns {string} Chaîne normalisée, ou chaîne vide si null/undefined
 */
export function normalizeString(value) {
  if (value === null || value === undefined || value === "") {
    return "";
  }
  return String(value).trim().toUpperCase();
}

/**
 * Parse une valeur en nombre
 * @param {any} value - Valeur à parser
 * @returns {number|null} Nombre parsé, ou null si invalide
 *
 * Gère les virgules comme séparateurs décimaux (format européen)
 */
export function parseNumber(value) {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  // Si déjà un nombre, le retourner directement
  if (typeof value === "number") {
    return isNaN(value) ? null : value;
  }

  // Convertir en string et remplacer virgule par point
  const strValue = String(value).trim().replace(",", ".");
  const parsed = parseFloat(strValue);

  return isNaN(parsed) ? null : parsed;
}

/**
 * Extrait les événements d'une ligne jusqu'à la première cellule vide
 * @param {Array} row - Ligne de données
 * @param {Array} eventColumns - Colonnes événements détectées
 * @returns {Array} Tableau de valeurs numériques
 *
 * Pourquoi s'arrêter à la première cellule vide ?
 * C'est le comportement défini : les événements sont consécutifs sans trous
 */
export function extractEvents(row, eventColumns) {
  const events = [];

  for (const eventCol of eventColumns) {
    const cellValue = row[eventCol.index];

    // Arrêter à la première cellule vide
    if (cellValue === "" || cellValue === null || cellValue === undefined) {
      break;
    }

    const numericValue = parseNumber(cellValue);

    if (numericValue !== null) {
      events.push(numericValue);
    } else {
      // Si parsing échoue, arrêter (cellule invalide)
      break;
    }
  }

  return events;
}

/**
 * Valide la présence des colonnes requises dans un header
 * @param {Array} headerRow - Ligne de headers
 * @param {Array} requiredColumnNames - Noms des colonnes requises
 * @returns {Object} {success: boolean, columnMap: Object, missingColumns: Array}
 */
export function validateRequiredColumns(headerRow, requiredColumnNames) {
  const columnMap = {};
  const missingColumns = [];

  // Mapping manuel des colonnes vers leurs clés
  const columnKeyMap = {
    "ID Câble": "ID_CABLE",
    "N° Fibre": "NUM_FIBRE",
    Couleur: "COULEUR",
    Lambda: "LAMBDA",
    "Moyenne connecteurs": "MOYENNE_CONNECTEURS",
    "Moyenne épissures": "MOYENNE_EPISSURES",
    "Perte totale": "PERTE_TOTALE",
    Moyenne: "MOYENNE",
  };

  requiredColumnNames.forEach((columnName) => {
    const index = findColumnIndex(headerRow, columnName);

    if (index !== -1) {
      const key = columnKeyMap[columnName];
      if (key) {
        columnMap[key] = index;
      }
    } else {
      missingColumns.push(columnName);
    }
  });

  return {
    success: missingColumns.length === 0,
    columnMap,
    missingColumns,
  };
}
